import { ExportCsv } from '@material-table/exporters'
import Paper from '@material-ui/core/Paper'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import axios from 'axios'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import MaterialTableCustom from '../../../components/MaterialTableCustom'
import PartPopup from '../../../components/PartPopup'
import { API_URL } from '../../../config'
import { useCTBPartsTable } from '../../../hooks/queries/ctbParts'

const useStyles = makeStyles(() => ({
  tablePaper: {
    fontFamily: 'Nunito',
    marginTop: 20,
    borderRadius: 20,
    paddingLeft: 10,
    paddingRight: 20,
    paddingBottom: 20,
    overflow: 'hidden',
    '& .MuiPaper-elevation2': {
      overflow: 'hidden',
      boxShadow: 'unset',
    },
    '& .MuiToolbar-gutters': {
      minHeight: 50,
      marginLeft: 10,
      marginTop: 10,
    },
  },
}))

const CTBPartsTable = ({ project, parentPart }) => {
  const classes = useStyles()
  const ctbPartsTableCellFontSize = 12
  // const [includeBacklog, setIncludeBacklog] = useState(false)

  const [ctbData, setCtbData] = useState({ data: [], columns: [] })
  const [popupAnchorEl, setPopupAnchorEl] = useState(null)
  const [partNumber, setPartNumber] = useState(null)

  function partNumberClicked(event, partNumber) {
    setPartNumber(partNumber)
    setPopupAnchorEl(event.currentTarget)
  }
  const formatColumns = (inputColumns) => {
    if (!inputColumns || !inputColumns.length) return []
    const outputColumns = [
      {
        title: 'Level',
        field: 'level',
        removable: false,
        cellStyle: { fontFamily: 'Nunito' },
        sorting: false,
        headerStyle: { width: 15, padding: 8 },
        render: function ctbParts(rowData) {
          const cellData = rowData['level']
          const spacedData = ' '.repeat(cellData * 5 + 2) + cellData
          const outerStyle = {
            display: 'flex',
            flexFlow: 'column nowrap',
            alignItems: 'center',
            justifyContent: 'center',
            width: 15,
            padding: 0,
            fontFamily: 'Nunito',
          }
          const innerStyle = {
            padding: 0,
            borderRadius: 5,
            fontFamily: 'Nunito',
            fontSize: 12,
            whiteSpace: 'pre',
          }
          return (
            <div className='outerCont' style={outerStyle}>
              <Typography varient='body1' style={innerStyle}>
                {spacedData}
              </Typography>
            </div>
          )
        },
      },
      {
        title: 'PN',
        field: 'pn',
        removable: false,
        headerStyle: {
          textAlign: 'center',
          width: 150,
          padding: 8,
        },
        cellStyle: {
          width: 150,
          whiteSpace: 'normal', // allow wrapping
          wordBreak: 'break-word', // handle long part numbers
        },
        render: (rowData) => {
          const styles = { cursor: 'pointer', textDecoration: 'underline' }
          return (
            <div
              className='text-hover'
              onClick={(event) => {
                partNumberClicked(event, rowData.pn)
              }}
            >
              <span style={styles}>{rowData.pn}</span>
            </div>
          )
        },
      },
      {
        title: 'Description',
        field: 'desc',
        cellStyle: { fontFamily: 'Nunito' },
        sorting: false,
        headerStyle: { width: 225, padding: 8 },
        render: function ctbParts(rowData) {
          const cellData = rowData['desc']
          const outerStyle = {
            display: 'flex',
            flexFlow: 'column nowrap',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            width: 225,
            padding: 0,
            fontFamily: 'Nunito',
          }
          const innerStyle = {
            padding: 0,
            borderRadius: 5,
            fontFamily: 'Nunito',
            fontSize: 12,
          }
          return (
            <div className='outerCont' style={outerStyle}>
              <Typography varient='body1' style={innerStyle}>
                {cellData}
              </Typography>
            </div>
          )
        },
      },
      {
        title: 'Quantity Required',
        field: 'qty',
        removable: false,
        cellStyle: { fontFamily: 'Nunito' },
        sorting: false,
        headerStyle: { width: 15, padding: 8 },
        render: function ctbParts(rowData) {
          const cellData = rowData['qty']
          const outerStyle = {
            display: 'flex',
            flexFlow: 'column nowrap',
            alignItems: 'center',
            justifyContent: 'center',
            width: 15,
            padding: 0,
            fontFamily: 'Nunito',
          }
          const innerStyle = {
            padding: 0,
            borderRadius: 5,
            fontFamily: 'Nunito',
            fontSize: 12,
          }
          return (
            <div className='outerCont' style={outerStyle}>
              <Typography varient='body1' style={innerStyle}>
                {cellData}
              </Typography>
            </div>
          )
        },
      },
      {
        title: 'Quantity Reserved',
        field: 'qtyReserved',
        removable: false,
        cellStyle: { fontFamily: 'Nunito' },
        sorting: false,
        headerStyle: { width: 15, padding: 8 },
        render: function ctbParts(rowData) {
          const cellData = rowData['qtyReserved']
          const outerStyle = {
            display: 'flex',
            flexFlow: 'column nowrap',
            alignItems: 'center',
            justifyContent: 'center',
            width: 15,
            padding: 0,
            fontFamily: 'Nunito',
          }
          const innerStyle = {
            padding: 0,
            borderRadius: 5,
            fontFamily: 'Nunito',
            fontSize: 12,
          }
          return (
            <div className='outerCont' style={outerStyle}>
              <Typography varient='body1' style={innerStyle}>
                {cellData}
              </Typography>
            </div>
          )
        },
      },
    ]

    // Always include these columns in output
    const addSimpleColumn = (field, title, removable = true, { highlightShortage = false } = {}) => {
      outputColumns.push({
        title: title,
        field: field,
        removable: removable,
        cellStyle: { fontFamily: 'Nunito' },
        sorting: false,
        headerStyle: { width: 15, padding: 8 },
        render: function ctbParts(rowData) {
          const rawValue = rowData[field]
          const cellData = rawValue
          const outerStyle = {
            display: 'flex',
            flexFlow: 'column nowrap',
            alignItems: 'center',
            justifyContent: 'center',
            width: 15,
            padding: 0,
            fontFamily: 'Nunito',
          }
          const innerStyle = {
            padding: 0,
            borderRadius: 5,
            fontFamily: 'Nunito',
            fontSize: 12,
          }
          const numVal = Number(rawValue)
          const makeRed = highlightShortage && !isNaN(numVal) && numVal < 0
          const dynStyle = {
            ...innerStyle,
            color: makeRed ? '#d32f2f' : undefined,
            fontWeight: makeRed ? 700 : undefined,
          }
          return (
            <div className='outerCont' style={outerStyle}>
              <Typography varient='body1' style={dynStyle}>
                {cellData}
              </Typography>
            </div>
          )
        },
      })
    }

    addSimpleColumn('shortage', 'Shortage', false, { highlightShortage: true })
    addSimpleColumn('spare', 'Spare')
    addSimpleColumn('mo', 'MO')

    // for (let i = 3; i < inputColumns.length; i++) {
    //   let qty = inputColumns[i]
    //   outputColumns.push({
    //     title: qty,
    //     field: qty,
    //     cellStyle: { fontFamily: 'Nunito' },
    //     sorting: false,
    //     headerStyle: { width: 15, padding: 8 },
    //     render: function ctbParts(rowData) {
    //       const cellData = rowData[qty]
    //       console.log(cellData)
    //       const outerStyle = {
    //         display: 'flex',
    //         flexFlow: 'column nowrap',
    //         alignItems: 'center',
    //         justifyContent: 'center',
    //         width: 15,
    //         padding: 0,
    //         fontFamily: 'Nunito',
    //       }
    //       const innerStyle = {
    //         padding: 0,
    //         borderRadius: 5,
    //         fontFamily: 'Nunito',
    //         fontSize: 12,
    //       }
    //       return (
    //         <div className='outerCont' style={outerStyle}>
    //           <Typography varient='body1' style={innerStyle}>
    //             {cellData}
    //           </Typography>
    //         </div>
    //       )
    //     },
    //   })
    // }
    return outputColumns
  }

  useCTBPartsTable(project, parentPart, setCtbData, formatColumns)
  const { data, columns } = ctbData

  return (
    <Paper className={clsx('mat-table-cont', classes.tablePaper)}>
      <MaterialTableCustom
        key={parentPart?.partNumber + (project ?? 'none')}
        title='CTB Parts Overview'
        columns={columns}
        data={data}
        options={{
          filtering: false,
          search: true,
          paging: false,
          maxBodyHeight: '700px',
          padding: '0',
          rowStyle: (rowData) => ({
            fontSize: `${ctbPartsTableCellFontSize}px`,
            textAlign: 'center',
            color: rowData?.parentFulfilled ? '#9e9e9e' : undefined,
            // backgroundColor: rowData?.parentFulfilled ? '#f5f5f5' : undefined,
          }),
        }}
      />
      <PartPopup project={project} parentPart={parentPart} partNumber={partNumber} setAnchorEl={setPopupAnchorEl} anchorEl={popupAnchorEl} />
    </Paper>
  )
}

CTBPartsTable.propTypes = {
  parentPart: PropTypes.object,
}

export default CTBPartsTable
